import { format } from "date-fns";
import { ChevronLeft } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../../components/ui/sheet";
import {
  dayLabel,
  isUrgentEntry,
  parseApiDate,
  toDisplayDate,
  toTitleCase,
  type CalendarDay,
  type CalendarEntry,
  type EntryType,
} from "./alertCalendarData";

export interface DrawerState {
  /** Day list, or a single entry's detail */
  kind: "day" | "detail";
  date: string;
  entry?: CalendarEntry;
  /** Detail reached from the day list — shows a back arrow */
  fromList?: boolean;
}

const TYPE_STYLE: Record<EntryType, { label: string; chip: string; bar: string; head: string }> = {
  REVIEW_DUE: {
    label: "Review due",
    chip: "bg-amber-500/[0.16] text-amber-800 dark:text-amber-300",
    bar: "bg-amber-600",
    head: "bg-amber-500/[0.08]",
  },
  NEW_ALERT: {
    label: "New alert",
    chip: "bg-violet-500/[0.14] text-violet-800 dark:text-violet-300",
    bar: "bg-violet-600",
    head: "bg-violet-500/[0.08]",
  },
  REMINDER: {
    label: "Reminder",
    chip: "bg-muted d",
    bar: "bg-muted-foreground",
    head: "bg-muted/40",
  },
};

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/60 py-2.5 last:border-b-0">
      <span className="text-[11.5px] d">{label}</span>
      <span className="break-words text-right text-[12px] font-bold text-foreground">
        {value}
      </span>
    </div>
  );
}

function Section({ children }: { children: string }) {
  return (
    <p className="mb-1 mt-5 text-[10.5px] font-extrabold tracking-wider d">
      {children}
    </p>
  );
}

/* ── Detail ─────────────────────────────────────────────────────────────── */

function Detail({
  entry,
  fromList,
  onBack,
}: {
  entry: CalendarEntry;
  fromList: boolean;
  onBack: () => void;
}) {
  /* Everything here comes off the list row, so the panel opens with no fetch,
     no spinner and nothing to fail. */
  const style = TYPE_STYLE[entry.entryType];
  const pct = Math.max(
    4,
    Math.min(100, (entry.dayIndex / Math.max(entry.totalDays, 1)) * 100),
  );

  const headline =
    entry.entryType === "REVIEW_DUE"
      ? "Review due today"
      : entry.entryType === "NEW_ALERT"
        ? `Alert raised - ${entry.daysRemaining} Days until review`
        : `Reminder - ${entry.daysRemaining} Days until review`;

  return (
    <>
      <div
        className={`flex flex-none items-start gap-2.5 border-b border-border py-4 pl-5 pr-14 ${style.head}`}
      >
        {fromList && (
          <button
            type="button"
            aria-label="Back to day"
            onClick={onBack}
            className="flex size-[26px] flex-none items-center justify-center rounded-[7px] border border-border bg-card d transition-colors hover:text-foreground"
          >
            <ChevronLeft className="size-4" />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-extrabold tracking-wider d">
            {style.label}
          </p>
          <SheetTitle className="mt-1 text-[16.5px] font-bold leading-tight text-foreground">
            {entry.title}
          </SheetTitle>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {/* Day counter */}
        <div className={`rounded-[10px] px-4 py-3.5 ${style.chip}`}>
          <p className="text-[14.5px] font-bold leading-tight">{headline}</p>
          <div className="relative my-3 h-[5px] overflow-hidden rounded-sm bg-black/15 dark:bg-white/15">
            <div
              className="absolute inset-y-0 left-0 rounded-sm bg-current"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-[10.5px] font-bold tabular-nums">
            {dayLabel(entry)}
          </p>
        </div>

        <Section>Document</Section>
        <Field label="Organization" value={entry.orgName} />
        <Field label="Category" value={toTitleCase(entry.documentCategory)} />
        <Field label="Document type" value={toTitleCase(entry.documentType)} />
        {/* The API sends a bare number; the UI prefixes it. */}
        <Field label="Version" value={entry.version != null ? `v${entry.version}` : "-"} />
        <Field label="Published date" value={toDisplayDate(entry.publishedDate)} />
        <Field label="Review date" value={toDisplayDate(entry.reviewDate)} />
        {/* reviewDate = publishedDate + reviewCycleDays. */}
        <Field
          label="Review cycle"
          value={entry.reviewCycleDays != null ? `${entry.reviewCycleDays} Days` : "-"}
        />
      </div>
    </>
  );
}

/* ── Day list ───────────────────────────────────────────────────────────── */

/** Splits the day into its two groups and counts each. */
function GroupHeading({
  label,
  count,
  tone,
  dot,
}: {
  label: string;
  count: number;
  tone: string;
  dot: string;
}) {
  return (
    <p
      className={`mb-2 mt-5 flex items-center gap-2 text-[10.5px] font-extrabold tracking-wider first:mt-0 ${tone}`}
    >
      <span className={`size-[7px] flex-none rotate-45 rounded-[1px] ${dot}`} />
      {label} - {count}
    </p>
  );
}

function DayList({
  day,
  onOpenEntry,
}: {
  day: CalendarDay;
  onOpenEntry: (entry: CalendarEntry) => void;
}) {
  const row = (entry: CalendarEntry) => {
    const style = TYPE_STYLE[entry.entryType];
    const dashed = entry.entryType === "REMINDER";
    return (
      <button
        key={entry.documentId}
        type="button"
        onClick={() => onOpenEntry(entry)}
        className={`mb-1.5 flex w-full items-center gap-2.5 rounded-[9px] border bg-card p-2.5 text-left transition-colors hover:border-indigo-400 hover:bg-muted/40 ${
          dashed ? "border-dashed border-border" : "border-border"
        }`}
      >
        <span
          className={`w-1 flex-none self-stretch rounded-sm ${
            dashed
              ? `opacity-45 ${isUrgentEntry(entry) ? "bg-amber-500" : "bg-blue-500"}`
              : style.bar
          }`}
        />
        <span className="min-w-0 flex-1">
          <span
            className={`inline-block rounded-[5px] px-2 py-0.5 text-[9px] font-extrabold tracking-wider ${style.chip}`}
          >
            {style.label}
          </span>
          <span className="mt-1 block truncate text-[12px] font-semibold text-foreground">
            {entry.title}
          </span>
          <span className="mt-0.5 block text-[10px] tabular-nums d">
            {entry.orgName} - {dayLabel(entry)}
          </span>
        </span>
        <span
          className={`flex-none rounded-full px-2 py-1 text-[9.5px] font-bold ${style.chip}`}
        >
          {entry.daysRemaining === 0 ? "due" : `${entry.daysRemaining}d`}
        </span>
      </button>
    );
  };

  /* One group per array, matching the grid — a window opening is not the same
     as one counting down. */
  const reviewDue = day.reviewDue ?? [];
  const newAlerts = day.newAlerts ?? [];
  const reminders = day.reminders ?? [];
  const total = reviewDue.length + newAlerts.length + reminders.length;

  return (
    <>
      <div className="flex flex-none items-start gap-2.5 border-b border-border py-4 pl-5 pr-14">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-extrabold tracking-wider d">
            {day.today ? "Today" : format(parseApiDate(day.date), "EEEE")}
          </p>
          <SheetTitle className="mt-1 text-[16px] font-bold tabular-nums text-foreground">
            {toDisplayDate(day.date)}
          </SheetTitle>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {total === 0 && (
          <p className="text-[11.5px] d">Nothing scheduled on this day.</p>
        )}

        {reviewDue.length > 0 && (
          <>
            <GroupHeading
              label="Review due"
              count={reviewDue.length}
              tone="text-amber-700 dark:text-amber-400"
              dot="bg-amber-600"
            />
            {reviewDue.map(row)}
          </>
        )}

        {newAlerts.length > 0 && (
          <>
            <GroupHeading
              label={`New alert${newAlerts.length > 1 ? "s" : ""}`}
              count={newAlerts.length}
              tone="text-violet-700 dark:text-violet-400"
              dot="bg-violet-600"
            />
            {newAlerts.map(row)}
          </>
        )}

        {reminders.length > 0 && (
          <>
            <GroupHeading
              label={`Reminder${reminders.length > 1 ? "s" : ""}`}
              count={reminders.length}
              tone="d"
              dot="bg-muted-foreground"
            />
            {reminders.map(row)}
          </>
        )}
      </div>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Drawer
   ══════════════════════════════════════════════════════════════════════════ */

export function AlertDrawer({
  state,
  day,
  onOpenChange,
  onOpenEntry,
  onBackToDay,
}: {
  state: DrawerState | null;
  day: CalendarDay | null;
  onOpenChange: (open: boolean) => void;
  onOpenEntry: (entry: CalendarEntry) => void;
  onBackToDay: (date: string) => void;
}) {
  return (
    <Sheet open={!!state} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex h-full w-[352px] max-w-[90vw] flex-col gap-0 border-l border-border bg-card p-0"
      >
        {state?.kind === "detail" && state.entry ? (
          <Detail
            entry={state.entry}
            fromList={!!state.fromList}
            onBack={() => onBackToDay(state.date)}
          />
        ) : state && day ? (
          <DayList day={day} onOpenEntry={onOpenEntry} />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
